import { h, Component, createRef, RefObject } from "preact";

import { stripHTML } from "../../apps/legacy/richText";
import CSSProperties from "../../foundation/model/CSSProperties";
import classname from "../../foundation/utils/classname";
import { NON_BREAKING_SPACE } from "../../foundation/utils/string";

import styles from "./ExpandableInput.module.css";

const DEFAULT_MAXLENGTH = 100;

function strokeLine(x1: number, x2: number, linecap: "butt" | "round") {
    return `<line x1="${x1}" y1="0.5" x2="${x2}" y2="0.5" stroke="%23419bf9" stroke-width="1" stroke-linecap="${linecap}"/>`;
}

function svg(...children: string[]) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="3" height="1">${children.join(" ")}</svg>`;
}

function getSelectedBorderImageSVG(hasLeftSelection: boolean, hasRightSelection: boolean) {
    if (hasLeftSelection && hasRightSelection) {
        return svg(strokeLine(0, 2.5, "butt"));
    } else if (hasLeftSelection) {
        return svg(strokeLine(0, 1.5, "butt"), strokeLine(1.5, 2.5, "round"));
    } else if (hasRightSelection) {
        return svg(strokeLine(0.5, 1.5, "round"), strokeLine(1.5, 2.5, "butt"));
    }
}

interface State {
    focusWithin: boolean;
    discard: boolean;
    selectionPoints: {
        selectionStart: number;
        selectionEnd: number;
    };
    shouldCheckRightSelection: boolean;
    value: string;
    editing: boolean; // only used when formatter is given in the props
}

interface DefaultProps {
    maxLength: number;
    tabIndex: number;
    required: boolean;
}

interface Props extends DefaultProps {
    class?: string;
    value: string;
    focusedValue?: string;
    fontStyle?: CSSProperties;
    readOnly?: boolean;
    showRemaining?: boolean;
    selectionPoints?: {
        selectionStart: number;
        selectionEnd: number;
    };
    formatter?: (value: string) => string;
    onAttemptToChange?: () => void;
    onBlur: (e: { discard: boolean; value: string; }) => void;
    "data-testid"?: string;
}

class ExpandableInput extends Component<Props, State> {
    static override defaultProps: DefaultProps = {
        required: false,
        maxLength: DEFAULT_MAXLENGTH,
        tabIndex: 0
    };

    override state = {
        focusWithin: false,
        discard: false,
        selectionPoints: this.props.selectionPoints ?? {
            selectionStart: 0,
            selectionEnd: 0
        },
        shouldCheckRightSelection: false,
        value: this.props.value,
        editing: false
    };

    textInputRef: RefObject<HTMLInputElement> = createRef();
    widthlessMirrorRef: RefObject<HTMLSpanElement> = createRef();
    inputContainerRef: RefObject<HTMLFormElement> = createRef();

    getSelectionPoints = () => {
        const { selectionStart, selectionEnd } = this.props.selectionPoints ?? this.textInputRef.current!;

        return {
            selectionStart: selectionStart!,
            selectionEnd: selectionEnd!
        };
    };

    getSelectionPointsFromStartToEnd = () => {
        const { value } = this.state;
        return {
            selectionStart: 0,
            selectionEnd: value.length
        };
    };

    getSelectionPointsFromEndToEnd = () => {
        const { value } = this.state;
        return {
            selectionStart: value.length,
            selectionEnd: value.length
        };
    };

    calculateShouldCheckRightSelection = () =>
        this.widthlessMirrorRef.current!.scrollWidth >= this.inputContainerRef.current!.offsetWidth;

    // Used outside this component
    highlight = () => {
        this.handleFocus();

        if (!this.props.selectionPoints) {
            this.setState({ selectionPoints: this.getSelectionPointsFromStartToEnd() });
        }
    };

    // Used outside this component
    highlightAndScrollTo = () => {
        this.inputContainerRef.current!.scrollIntoView();
        this.highlight();
    };

    handleBlur = () => {
        const { required, value: oldValue, readOnly, onBlur } = this.props;

        // `focusWithin` check is added to solve that `handleBlur` is called before `handleFocus`
        // when user selects the text and leaves it outside the component on Chrome.
        const { focusWithin } = this.state;
        if (readOnly || !focusWithin) {
            return;
        }

        const { value: newValue } = this.state;
        let { discard } = this.state;

        const trimmedNewValue = newValue.trim();

        const newState: Partial<State> = {
            focusWithin: false,
            editing: false,
            value: trimmedNewValue
        };

        if (required && !trimmedNewValue) {
            newState.value = oldValue;
            discard = true;
        }

        this.setState(newState);

        onBlur({ discard, value: trimmedNewValue });
    };

    handleFocus = () => {
        const { readOnly, focusedValue } = this.props;

        if (readOnly) {
            return;
        }

        if (focusedValue) {
            this.setState({ value: focusedValue });
        }

        this.setState({ focusWithin: true, discard: false, editing: true });
    };

    handleInput = (ev: Event) => {
        const { onAttemptToChange } = this.props;

        if (onAttemptToChange) {
            onAttemptToChange();
            return;
        }

        const newValue = (ev.target as HTMLTextAreaElement).value;

        if (newValue === this.state.value) {
            return;
        }

        const selectionPoints = this.getSelectionPoints();
        this.setState({ value: newValue, selectionPoints });
    };

    handleKeyDown = (ev: KeyboardEvent) => {
        switch (ev.keyCode) {
            case Zeplin.KEY_CODE.ESC: {
                ev.stopPropagation();

                const { value } = this.props;
                this.setState({
                    value,
                    discard: true
                }, () => {
                    this.textInputRef.current!.blur();
                });
                break;
            }
            default:
                // no-op
        }
    };

    handleMouseDown = (ev: MouseEvent) => {
        if (ev.button === Zeplin.MOUSE_BUTTON.LEFT) {
            ev.stopPropagation();
        }
    };

    handleClick = (ev: MouseEvent) => {
        ev.stopPropagation();
        this.handleSelectionChange();
        this.handleFocus();
    };

    handleFormattedTextClick = (ev: Event) => {
        ev.stopPropagation();
        if ((ev.target as HTMLElement).closest("a")) {
            return;
        }

        this.handleFocus();

        if (!this.props.selectionPoints) {
            this.setState({ selectionPoints: this.getSelectionPointsFromEndToEnd() });
        }
    };

    handleSubmit = (ev: Event) => {
        ev.preventDefault();
        this.textInputRef.current!.blur();
    };

    handleSelectionChange = () => {
        const selectionPoints = this.getSelectionPoints();
        this.setState({ selectionPoints });
    };

    override componentDidMount() {
        const { editing } = this.state;
        const { value } = this.props;

        if (editing) {
            this.textInputRef.current!.focus();
            this.textInputRef.current!.setSelectionRange(value.length, value.length);
        }

        this.setState({
            shouldCheckRightSelection: this.calculateShouldCheckRightSelection()
        });
    }

    override componentWillReceiveProps(nextProps : Props) {
        if (!this.state.focusWithin && nextProps.value !== this.state.value) {
            this.setState({ value: nextProps.value });
        }
    }

    override componentDidUpdate(_props: Props, prevState: State) {
        const { editing, selectionPoints, value, focusWithin } = this.state;

        if (editing && prevState.editing !== editing) {
            this.textInputRef.current!.focus();
            this.textInputRef.current!.setSelectionRange(selectionPoints.selectionStart, selectionPoints.selectionEnd);

            if (this.props.selectionPoints) {
                this.textInputRef.current!.scrollLeft = this.textInputRef.current!.scrollWidth;
            }
        }

        if (value !== prevState.value) {
            this.setState({
                shouldCheckRightSelection: this.calculateShouldCheckRightSelection()
            });
        }

        if (!focusWithin && value !== this.props.value) {
            this.setState({ value: this.props.value });
        }
    }

    render() {
        const {
            class: cssClass,
            required,
            readOnly,
            maxLength,
            showRemaining,
            fontStyle,
            tabIndex,
            formatter
        } = this.props;

        const {
            focusWithin,
            value,
            selectionPoints,
            shouldCheckRightSelection,
            editing
        } = this.state;

        const formattedContent = formatter ? formatter(value) : value;

        const style = Object.assign({}, fontStyle);
        if (focusWithin && !readOnly) {
            const { selectionStart, selectionEnd } = selectionPoints;
            const hasLeftSelection = selectionStart !== selectionEnd && selectionStart === 0;
            const hasRightSelection = shouldCheckRightSelection &&
                selectionStart !== selectionEnd && selectionEnd === value.length;
            const borderImageSVG = getSelectedBorderImageSVG(hasLeftSelection, hasRightSelection);
            if (borderImageSVG) {
                Object.assign(style, {
                    borderImageSource: `url('data:image/svg+xml;utf8,${borderImageSVG}')`,
                    borderImageSlice: "0 1 1 1",
                    borderImageRepeat: "stretch"
                });
            }
        }

        const showFormattedText = Boolean(formatter) && !editing;
        const mirrorText = (showFormattedText ? stripHTML(formattedContent) : value) || NON_BREAKING_SPACE;

        return (
            <div class={classname(styles.expandableInputWrapper, cssClass)}>
                <form ref={this.inputContainerRef} class={styles.expandableInputForm} onSubmit={this.handleSubmit}>
                    <span
                        ref={this.widthlessMirrorRef}
                        class={classname(styles.mirror, styles.widthless)}
                        style={fontStyle}>
                        {mirrorText}
                    </span>
                    <span class={styles.mirror} style={fontStyle}>{mirrorText}</span>
                    {showFormattedText && (
                        <div
                            onClick={this.handleFormattedTextClick}
                            class={styles.formattedContent}
                            style={style}
                            dangerouslySetInnerHTML={{ __html: formattedContent }}>
                        </div>
                    )}
                    {!showFormattedText && (
                        <input
                            style={style}
                            type="text"
                            name="expandableInput"
                            data-testid={this.props["data-testid"]}
                            minLength={1}
                            maxLength={maxLength}
                            autocomplete="off"
                            spellcheck={false}
                            required={required}
                            value={value}
                            readOnly={readOnly}
                            tabIndex={tabIndex}
                            ref={this.textInputRef}
                            onMouseDown={this.handleMouseDown}
                            onClick={this.handleClick}
                            onSelect={this.handleSelectionChange}
                            onKeyDown={this.handleKeyDown}
                            onInput={this.handleInput}
                            onBlur={this.handleBlur}/>
                    )}
                </form>
                {showRemaining && focusWithin && (
                    <span class={styles.remainingCounter}>
                        {maxLength - value.length}
                    </span>
                )}
            </div>
        );
    }
}

export default ExpandableInput;
export { Props as ExpandableInputProps };
